import { useGlowQr } from '../../hooks/useGlowQr'
import { Button, buttonClasses } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import { ErrorBanner } from '../../components/ErrorBanner'
import { apiErrorMessage } from '../../api/client'
import { glowUrl, glowHost } from '../../lib/glowUrl'
import type { Glow } from '../../api/types'

// QrCodeDialog previews the branded QR code for a glow (a QR linking to the
// glow's live root URL, with the Backlit lockup centered) and offers a
// Download PNG action. The QR is generated server-side (console-api
// GET /v1/glows/{id}/qr?format=json → { png_base64 }); this dialog renders it
// as a `data:image/png;base64,...` URL — the console apiFetch only decodes
// JSON, so there is deliberately no blob/createObjectURL path. Free for
// everyone: no subscription gating in the UI.
export function QrCodeDialog({
  glow,
  onClose,
}: {
  glow: Glow
  onClose: () => void
}) {
  // Mount-on-open: the dialog only renders when open, so `enabled` is always
  // true here — the gate exists so the query doesn't fire until this component
  // mounts (no eager fetch on every glow-detail view).
  const { data, isLoading, error } = useGlowQr(glow.id, true)
  const dataUrl = data ? `data:image/png;base64,${data.png_base64}` : ''

  return (
    <Modal onClose={onClose} labelledBy="qr-dialog-title">
      <h3 id="qr-dialog-title" className="text-lg font-semibold">
        QR code
      </h3>
      <p className="mt-2 text-sm text-cream/70">
        A scannable code linking to your glow. Download it to print or share.
      </p>

      {isLoading && (
        <div className="mt-4 flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {error && (
        <ErrorBanner className="mt-4">
          {apiErrorMessage(error, 'Could not generate QR code.')}
        </ErrorBanner>
      )}

      {data && (
        <>
          <div className="mt-4 flex justify-center">
            <img
              src={dataUrl}
              alt={`QR code linking to ${glowHost(glow.name)}`}
              className="h-64 w-64 max-w-full rounded-md bg-white"
            />
          </div>
          <p className="mt-3 break-all text-center text-xs text-muted">
            {glowUrl(glow.name)}
          </p>
        </>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {data && (
          <a
            href={dataUrl}
            download={`${glow.name}-qr.png`}
            className={buttonClasses('primary')}
          >
            Download PNG
          </a>
        )}
      </div>
    </Modal>
  )
}
